import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Header from "../components/app/Header";
import Footer from "../components/app/Footer";
import AnnouncementBar from "../components/app/AnnouncementBar";
import ShareMenu from "../components/app/ShareMenu";
import FeaturedCampersRail from "../components/app/FeaturedCampersRail";
import WildHiddenRail from "../components/app/WildHiddenRail";
import InstallPwaCard from "../components/app/InstallPwaCard";
import VisitorCounter from "../components/app/VisitorCounter";
import LiveBrowsingTicker from "../components/app/LiveBrowsingTicker";
import WeekendRegionPoll from "../components/app/WeekendRegionPoll";
import LastWeekResultsBanner from "../components/app/LastWeekResultsBanner";
import WelcomePollPrompt from "../components/app/WelcomePollPrompt";
import DirectBookPartnersTile from "../components/app/DirectBookPartnersTile";
import MostVisitedTile from "../components/app/MostVisitedTile";
import FestivalsHomepageStrip from "../components/app/FestivalsHomepageStrip";
import FeaturedReelStrip from "../components/app/FeaturedReelStrip";
import FisheriesAnnouncementRibbon from "../components/app/FisheriesAnnouncementRibbon";
import FestivalRibbon from "../components/app/FestivalRibbon";
import SummerBookNowRibbon from "../components/app/SummerBookNowRibbon";
import CondensedTopStrip from "../components/app/CondensedTopStrip";
import BirthdayBanner from "../components/app/BirthdayBanner";
import BirthdayCheerCard from "../components/app/BirthdayCheerCard";
import SignupNudgeModal from "../components/app/SignupNudgeModal";
import TrekExpertPromoBanner from "../components/app/TrekExpertPromoBanner";
import HomeSearchWidget from "../components/app/HomeSearchWidget";
import HomeCommunityPrompt from "../components/app/HomeCommunityPrompt";
import PromoBanner from "../components/app/PromoBanner";
import { FeedbackButton } from "../components/app/FeedbackWidget";
import PollNudge from "../components/app/PollNudge";
import { api } from "../lib/api";
import { captureSignupSource } from "../lib/signupSource";
import {
  ArrowRight, Users, MessageCircle, HeartHandshake, Sparkles,
  MapPin, Sliders, Scale, Shield, Compass as CompassIcon, Flame, Tent, Image as ImageIcon, Camera, Plus, Globe
} from "lucide-react";

const HERO = "https://static.prod-images.emergentagent.com/jobs/960655e7-663c-4e39-8635-d583fe372428/images/20ac7e9816971e28633ddf2bfde1e3c94f0f62cddd476f09a1a903bb8c03572c.png";

export default function Landing() {
  const { t } = useTranslation();
  const [threads, setThreads] = useState([]);
  // Site-count: load from localStorage on first paint so returning
  // visitors NEVER see the "11,000-flash → 11,105 real number" jump.
  // First-ever visitors briefly see `null` (renders the eyebrow "UK"
  // text with no leading number) until /campsites/stats lands —
  // typically <100 ms — then it animates in. The number is re-cached
  // after every fetch so it stays fresh for next time. Iter54.
  const [count, setCount] = useState(() => {
    try {
      const v = parseInt(localStorage.getItem("cc_total_count") || "0", 10);
      return v > 0 ? v : null;
    } catch { return null; }
  });
  const [bookableNow, setBookableNow] = useState(() => {
    try {
      const v = parseInt(localStorage.getItem("cc_bookable_now") || "0", 10);
      return v > 0 ? v : null;
    } catch { return null; }
  });
  const [stories, setStories] = useState([]);

  useEffect(() => {
    // Capture signup attribution as soon as the visitor lands — even if they
    // poke around for 10 minutes before tapping Sign Up, sessionStorage keeps
    // the ?ref= / utm / referrer for us.
    captureSignupSource();
    Promise.all([
      api.get("/community/threads").then((r) => setThreads(r.data.slice(0, 3))).catch(() => {}),
      api.get("/campsites/stats").then((r) => {
        if (typeof r.data?.total === "number") {
          setCount(r.data.total);
          try { localStorage.setItem("cc_total_count", String(r.data.total)); } catch {}
        }
        if (typeof r.data?.bookable_now === "number") {
          setBookableNow(r.data.bookable_now);
          try { localStorage.setItem("cc_bookable_now", String(r.data.bookable_now)); } catch {}
        }
      }).catch(() => {}),
      api.get("/wall", { params: { limit: 12 } }).then((r) => {
        // Only show real user trip stories in the Landing preview grid.
        // Filters out system seed posts (which have no campsite_name) so
        // the 2×2 mobile grid fills cleanly with photo-less trips too —
        // the fallback tile (camera icon on orange gradient) covers them.
        const items = r.data.items || [];
        const real = items.filter((s) => {
          const name = s.campsite_name;
          return typeof name === "string" && name.trim().length > 0 && !s.system;
        });
        setStories(real.slice(0, 6));
      }).catch(() => {}),
    ]);
  }, []);

  return (
    <div className="min-h-screen bg-[#fafafa]" data-testid="landing-page">
      {/* iter143 — Condensed top strip: replaces the 4 previously-stacked
          ribbons (weather + festival + announcement + fisheries) with a
          single rotating line that also carries small standout social
          icons and a compact "Sign up free" pill on the right. Saves
          ~120px of vertical chrome above the hero. */}
      <CondensedTopStrip />
      {/* iter170 — Trek Expert adidas ADI10 promo. Slim dismissible strip
          above the header, routed through /api/affiliate/redirect so clicks
          attribute to the operator's AWIN publisher ID with clickref=ADI10. */}
      <TrekExpertPromoBanner />
      {/* iter171 — Partner shops strip now lives inside <Header /> so it
          renders on every page (not just Landing). Nothing needed here. */}
      <Header compact />
      {/* iter169 — anonymous-visitor signup nudge, self-hides for logged-in
          users + respects a 3-day cool-down after dismiss */}
      <SignupNudgeModal />
      {/* iter163 — Newspaper partner promo hero (National Trust, RSPB, EH…).
          Self-hides when no promo is inside its preview→valid window. */}
      <PromoBanner />
      {/* iter130 — Birthday banner: only renders on days when at least one
          camper's birthday is today. Silent 364 days a year. */}
      <div className="max-w-[1400px] mx-auto px-3 sm:px-6 lg:px-10 pt-4">
        <BirthdayBanner />
        {/* iter165 — Interactive companion: prompts every member to send an
            emoji cheer (hug / high-five / party) to today's birthday camper.
            Silent alongside the banner when there are no birthdays. */}
        <BirthdayCheerCard />
      </div>

      {/* iter138 — Featured reel promoted to the top of the page so it's
          one of the first things a visitor sees. Auto-hides when the
          admin disables the tile so an empty slot never appears above
          the hero. */}
      <FeaturedReelStrip />

      {/* HERO — full-bleed photo behind the writing */}
      <section className="relative overflow-hidden border-b hairline" data-testid="landing-hero">
        {/* Live tickers — top-right of the hero. Stacked on mobile so the
            two pills (visits total + live "now browsing") never collide. */}
        <div className="absolute top-4 right-4 lg:top-6 lg:right-8 z-20 flex flex-col gap-2 items-end">
          <LiveBrowsingTicker />
          <VisitorCounter />
        </div>
        {/* Background image — anchored RIGHT so the subject (tent/chair) sits
            on the right half and never collides with the text column on the
            left. Slightly brightened so the campsite scene stays vivid. */}
        <img
          src={HERO}
          alt="Campsite at dawn"
          className="absolute inset-0 w-full h-full object-cover object-[70%_center] sm:object-[68%_center] md:object-[65%_center] lg:object-[62%_center]"
          style={{ filter: "brightness(1.15) saturate(1.1) contrast(1.05)" }}
          data-testid="landing-hero-bg"
        />
        {/* Overlay: dark on the left so text always reads — fades smoothly to
            transparent on the right where the photo subject lives. Strong
            contrast so the H1, body copy, and CTA buttons all stay readable. */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "linear-gradient(105deg, rgba(0,0,0,0.78) 0%, rgba(0,0,0,0.62) 28%, rgba(0,0,0,0.35) 48%, rgba(0,0,0,0.10) 68%, rgba(0,0,0,0) 88%)",
          }}
          aria-hidden="true"
        />
        {/* Soft bottom fade for the Campfire Code card */}
        <div
          className="absolute inset-x-0 bottom-0 h-40 pointer-events-none bg-gradient-to-t from-black/55 to-transparent"
          aria-hidden="true"
        />
        {/* Subtle grain */}
        <div
          className="absolute inset-0 opacity-[0.06] mix-blend-overlay pointer-events-none"
          style={{
            backgroundImage:
              "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>\")",
          }}
          aria-hidden="true"
        />

        <div className="relative min-h-[92vh] flex items-center">
          <div className="max-w-[1400px] w-full mx-auto px-6 lg:px-10 pt-20 pb-32 lg:py-28">
            <div className="max-w-3xl cc-fade text-white">
              <div className="eyebrow mb-6 !text-white drop-shadow-[0_1px_8px_rgba(0,0,0,0.8)]" data-testid="landing-eyebrow">
                {t("landing.eyebrow")}
              </div>
              <h1 className="font-display font-semibold text-5xl sm:text-6xl lg:text-7xl leading-[0.95] mb-8 drop-shadow-[0_2px_32px_rgba(0,0,0,0.85)]">
                {t("landing.h1_line1")}<br/>
                <span className="text-[#ff6a2c]">{t("landing.h1_line2")}</span>
              </h1>
              <p className="max-w-xl text-[19px] sm:text-[20px] font-medium leading-[1.55] text-white mb-5 drop-shadow-[0_1px_10px_rgba(0,0,0,0.85)]">
                {t("landing.intro_part1")} <strong className="text-[#ff6a2c]">{t("landing.intro_kind")}</strong> {t("landing.intro_part2")}
                <strong className="text-white"> {t("landing.intro_allAges")}</strong>.
              </p>
              <p className="max-w-xl text-[16px] sm:text-[17px] leading-[1.6] text-white/95 mb-3 drop-shadow-[0_1px_10px_rgba(0,0,0,0.85)]">
                {t("landing.personas")}
              </p>
              <p className="max-w-xl text-[16px] sm:text-[17px] leading-[1.6] text-white/95 mb-10 drop-shadow-[0_1px_10px_rgba(0,0,0,0.85)]">
                {t("landing.compareDb_part1")} <strong className="text-white">{t("landing.compareDb_strong")}</strong>
                {" "}{t("landing.compareDb_part2", { count: count != null ? count.toLocaleString() : "11,000+" })}
              </p>
              {/* iter126 — Simplified campsite search widget. Deep-links to /browse with all matching filters. */}
              <HomeSearchWidget />
              <div className="flex flex-wrap gap-3">
                <Link to="/community" className="btn-primary inline-flex items-center gap-2" data-testid="cta-community">
                  <Flame className="w-4 h-4" strokeWidth={2} />
                  {t("landing.joinCampfire")} <ArrowRight className="w-4 h-4" />
                </Link>
                <Link
                  to="/browse"
                  className="inline-flex items-center gap-2 px-5 py-3 bg-white/15 backdrop-blur border border-white/40 text-white uppercase tracking-wider text-xs font-bold hover:bg-white/25 transition-colors"
                  data-testid="cta-browse"
                >
                  <Tent className="w-4 h-4" strokeWidth={2} />
                  {t("landing.browseCampsites")}
                </Link>
                <Link
                  to="/wall?new=1"
                  className="inline-flex items-center gap-2 px-5 py-3 bg-[#fde047] text-[#1c1917] uppercase tracking-wider text-xs font-bold border-2 border-[#1c1917] shadow-[0_4px_0_0_#111] hover:shadow-[0_2px_0_0_#111] hover:translate-y-[2px] transition-all"
                  data-testid="cta-share-trip"
                >
                  <Sparkles className="w-4 h-4" strokeWidth={2} />
                  {t("landing.shareYourTripCta")}
                </Link>
                <ShareMenu
                  url={process.env.REACT_APP_PUBLIC_URL || (typeof window !== "undefined" ? window.location.origin : "")}
                  title="CampCompass UK · honest UK campsite comparisons"
                  text={`Found this — the UK's largest campsite database${count ? ` (${count.toLocaleString()} sites)` : ""} in one app. Real prices, real reviews, no affiliate spin. Free to browse, £1.99/mo for Pro. Try it →`}
                  buttonClassName="inline-flex items-center gap-2 px-5 py-3 bg-white text-[#ff4500] uppercase tracking-wider text-xs font-bold border-2 border-white shadow-[0_4px_0_0_#111] hover:shadow-[0_2px_0_0_#111] hover:translate-y-[2px] transition-all"
                  buttonLabel={t("landing.inviteFriend")}
                  testId="cta-invite-friend"
                  preferGrid
                />
              </div>
              <div className="mt-4 text-sm text-white/95 drop-shadow-[0_1px_8px_rgba(0,0,0,0.85)] flex flex-wrap items-center gap-x-2 gap-y-1" data-testid="hero-signup-callout">
                <span className="inline-flex items-center gap-1.5 font-bold tracking-wide">
                  <Sparkles className="w-3.5 h-3.5 text-[#ffe066]" /> Free to sign up · unlock every feature
                </span>
                <span className="text-white/70">·</span>
                <span>No card required · takes 30 seconds</span>
              </div>
              <div className="mt-12 grid grid-cols-3 gap-2 sm:gap-6 max-w-2xl drop-shadow-[0_2px_12px_rgba(0,0,0,0.85)]" data-testid="hero-stats">
                <Stat k={count != null ? count.toLocaleString() : "—"} v={t("landing.stat_sites")} light />
                <Stat k={bookableNow != null ? `${bookableNow.toLocaleString()}+` : "—"} v={t("landing.stat_bookable")} light />
                <Stat k="Free" v="Sign up in 30s" light />
              </div>
            </div>
          </div>
        </div>

        {/* Campfire Code ribbon — floats over hero on desktop, sits in flow on mobile */}
        <div className="lg:absolute lg:bottom-10 lg:left-1/2 lg:-translate-x-1/2 lg:max-w-md mx-6 -mt-6 mb-6 lg:my-0 lg:mx-0 bg-white/95 backdrop-blur border hairline p-4 flex items-center gap-3 shadow-[0_6px_0_0_#111] relative z-10" data-testid="campfire-code-card">
          <HeartHandshake className="w-5 h-5 text-[#ff4500] shrink-0" strokeWidth={1.5} />
          <div>
            <div className="eyebrow">{t("landing.campfireCode")}</div>
            <div className="text-sm text-[#111]">{t("landing.campfireCodeBody")}</div>
          </div>
        </div>
      </section>

      {/* iter122 — Festival hub strip moved here per user feedback ("nearer
          the top to avoid scrolling"). Sits directly under the hero so a
          festival-goer sees the relevant value prop without scrolling past
          the Install/poll cards. Auto-hides when no upcoming festivals. */}
      <FestivalsHomepageStrip />



      {/* iter129 — Community engagement pop-up: nudges visitors to share a
          trip story or start a thread. Positioned high on the home page so
          it's above-the-fold on mobile, dismissible for 7 days per user. */}
      <HomeCommunityPrompt />
      <FeedbackButton />
      <PollNudge />

      {/* Install PWA — moved to TOP after hero so non-tech users see it
          before scrolling past it. Friendly, plain-English copy. */}
      <InstallPwaCard />

      {/* Last-week results recap — only renders for the first 24 hours
          of each new poll week (and only if last week got any votes).
          Auto-hides outside that window so the page stays clean. */}
      <LastWeekResultsBanner />

      {/* Weekend region poll — anonymous voting, live counts visible to all.
          Tapping a region casts a vote AND routes to /browse?region=<region>
          so the user immediately sees matching campsites. */}
      <WeekendRegionPoll />

      {/* iter99 — One-time welcome prompt for newly-signed-up campers
          nudging them to cast their first poll vote so we know which
          region to drop them on. Self-dismisses after 24h or one tap. */}
      <WelcomePollPrompt />

      {/* iter100 — TRIP WALL PREVIEW — photo-led, drives users to the Wall.
          Moved up here (directly under the weekend poll) so engagement
          surfaces appear back-to-back at the top of the page. */}
      <section className="border-b hairline-strong bg-black text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-20" style={{
          backgroundImage: "radial-gradient(circle at 30% 20%, #ff4500 0px, transparent 60%), radial-gradient(circle at 80% 80%, #ff4500 0px, transparent 50%)",
        }} />
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 py-20">
          <div className="flex items-end justify-between flex-wrap gap-6 mb-10">
            <div>
              <div className="eyebrow !text-[#ffb89a] mb-3">{t("landing.wallEyebrow")}</div>
              <h2 className="font-display text-3xl sm:text-4xl tracking-tight max-w-2xl">
                {t("landing.wallHeadline_a")} <span className="text-[#ff4500]">{t("landing.wallHeadline_b")}</span>
              </h2>
              <p className="mt-3 text-sm text-white/70 max-w-xl leading-snug">
                {t("landing.wallBody")}
              </p>
            </div>
            <Link
              to="/wall"
              className="text-sm link-underline flex items-center gap-1 text-[#ff4500] hover:text-white"
              data-testid="landing-wall-link"
            >
              {t("landing.seeTheWall")} <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>

          {stories.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2" data-testid="landing-wall-grid">
              {stories.map((s, i) => {
                const cover = s.photos?.[0]?.image_data_url;
                return (
                  <Link
                    key={s.story_id}
                    to="/wall"
                    className="group relative aspect-[3/4] overflow-hidden border hairline bg-[#1a1a1a]"
                    data-testid={`landing-wall-${i}`}
                  >
                    {cover ? (
                      <img src={cover} alt={s.campsite_name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" loading="lazy" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-[#ff4500] to-[#a82e00]">
                        <Camera className="w-8 h-8 text-white/60" strokeWidth={1.5} />
                      </div>
                    )}
                    <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/90 to-transparent p-2 sm:p-3">
                      <div className="font-display text-xs sm:text-sm leading-tight text-white line-clamp-2">{s.campsite_name}</div>
                      <div className="text-[10px] mono text-white/60 truncate">— {s.user_name}</div>
                    </div>
                  </Link>
                );
              })}
            </div>
          ) : (
            <div className="bg-white/5 border-2 border-dashed border-white/20 p-10 text-center">
              <Camera className="w-10 h-10 text-[#ff4500] mx-auto mb-3" strokeWidth={1.5} />
              <p className="text-sm text-white/80 mb-4">{t("landing.wallEmptyTitle")}</p>
              <Link
                to="/wall?new=1"
                className="inline-flex items-center gap-2 bg-[#ff4500] text-white px-4 py-2.5 text-xs uppercase tracking-wider font-bold shadow-[0_4px_0_0_white] hover:translate-y-[2px] hover:shadow-[0_2px_0_0_white] transition-all"
                data-testid="landing-wall-empty-cta"
              >
                <Plus className="w-3.5 h-3.5" strokeWidth={2.5} /> {t("landing.beFirst")}
              </Link>
            </div>
          )}

          <div className="mt-8 flex flex-wrap items-center gap-3">
            <Link
              to="/wall?new=1"
              className="bg-[#ff4500] text-white font-bold uppercase tracking-wider text-sm px-5 py-3 inline-flex items-center gap-2 shadow-[0_4px_0_0_white] hover:shadow-[0_2px_0_0_white] hover:translate-y-[2px] transition-all"
              data-testid="landing-wall-share-cta"
            >
              <Plus className="w-4 h-4" strokeWidth={2.5} /> {t("landing.shareYourTrip")}
            </Link>
            <span className="text-[11px] mono text-white/60">
              <ImageIcon className="w-3 h-3 inline mr-1" strokeWidth={1.75} />
              {t("landing.wallOptIn")}
            </span>
          </div>
        </div>
      </section>

      {/* iter121 — Direct-book partners tile (Frowen Fields, Orcaber, …) */}
      <DirectBookPartnersTile />

      {/* iter121 — Most visited last 24h tile (auto-hides when no views) */}
      <MostVisitedTile />

      {/* THREE PILLARS */}
      <section className="border-b hairline bg-white">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20">
          <div className="eyebrow mb-4">{t("landing.pillar01")}</div>
          <h2 className="font-display text-3xl sm:text-4xl tracking-tight mb-12 max-w-2xl">
            {t("landing.pillarHeadline")}
          </h2>
          <div className="grid md:grid-cols-3 gap-0 border hairline">
            <Feat icon={Users} title={t("landing.pillar_community_t")}
              body={t("landing.pillar_community_b")} />
            <Feat icon={HeartHandshake} title={t("landing.pillar_safe_t")}
              body={t("landing.pillar_safe_b")} border />
            <Feat icon={Sparkles} title={t("landing.pillar_ai_t")}
              body={t("landing.pillar_ai_b")} border />
          </div>
        </div>
      </section>

      {/* LIVE COMMUNITY PREVIEW */}
      <section className="border-b hairline bg-[#fafafa]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20">
          <div className="flex items-end justify-between flex-wrap gap-6 mb-10">
            <div>
              <div className="eyebrow mb-3">{t("landing.live02")}</div>
              <h2 className="font-display text-3xl sm:text-4xl tracking-tight max-w-2xl">{t("landing.liveHeadline")}</h2>
            </div>
            <Link to="/community" className="text-sm link-underline flex items-center gap-1 text-[#ff4500]">
              {t("landing.allThreads")} <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>

          {threads.length > 0 ? (
            <div className="grid md:grid-cols-3 gap-0 border hairline bg-white" data-testid="landing-threads">
              {threads.map((t, i) => (
                <Link key={t.thread_id} to={`/community/${t.thread_id}`}
                  className={`p-6 block hover:bg-[#fffdfa] ${i > 0 ? "border-l hairline" : ""}`}
                  data-testid={`landing-thread-${i}`}>
                  <div className="flex items-center gap-2 mb-2">
                    <MessageCircle className="w-3.5 h-3.5 text-[#ff4500]" strokeWidth={1.75} />
                    <span className="px-2 py-0.5 text-[10px] uppercase tracking-wider border hairline mono">{t.tag}</span>
                  </div>
                  <div className="font-display text-lg leading-tight mb-2">{t.title}</div>
                  <p className="text-sm text-[#555] line-clamp-3">{t.body}</p>
                  <div className="mono text-[11px] text-[#888] mt-3">
                    {t.user_name} · {t.reply_count} replies · {t.kudos || 0} kudos
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="p-10 border hairline bg-white text-center text-sm text-[#555]">
              {t("landing.campfireLoading")}
            </div>
          )}
        </div>
      </section>

      {/* TRIP WALL PREVIEW relocated above (right under the weekend poll). */}

      {/* TESTIMONIAL VOICES */}
      <section className="border-b hairline bg-white">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20">
          <div className="eyebrow mb-4">{t("landing.voices03")}</div>
          <div className="grid md:grid-cols-3 gap-8 mt-10">
            {[
              { q: "First time camping with two kids and a dog. The community told me exactly which pitch at Castlerigg Hall to request. Trip was brilliant.", who: "Sarah · Manchester" },
              { q: "Asked about accessibility for my wheelchair at a Pembrokeshire site. Got three thoughtful answers within the hour. That's not normal on the internet.", who: "James · Cardiff" },
              { q: "I'm a solo camper. Knowing I can post a question and get kindness back makes me try sites I'd never have booked alone.", who: "Priya · Bristol" },
            ].map((t, i) => (
              <figure key={i} className="border hairline bg-[#fafafa] p-6" data-testid={`testimonial-${i}`}>
                <blockquote className="font-display text-lg leading-snug mb-4">"{t.q}"</blockquote>
                <figcaption className="eyebrow">{t.who}</figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* TOOLS — now framed as supporting the community */}
      <section className="border-b hairline bg-[#fafafa]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20">
          <div className="eyebrow mb-4">{t("landing.tools04")}</div>
          <h2 className="font-display text-3xl sm:text-4xl tracking-tight mb-12 max-w-2xl">{t("landing.toolsHeadline")}</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-0 border hairline bg-white">
            <Feat icon={MapPin} title={t("landing.tool_map_t")} body={t("landing.tool_map_b")} />
            <Feat icon={Sliders} title={t("landing.tool_filter_t")} body={t("landing.tool_filter_b")} border />
            <Feat icon={Scale} title={t("landing.tool_compare_t")} body={t("landing.tool_compare_b")} border />
            <Feat icon={Shield} title={t("landing.tool_reviews_t")} body={t("landing.tool_reviews_b")} border />
          </div>
        </div>
      </section>

      {/* MULTILINGUAL CHATBOT CHIP — surfaces the EN/FR/DE Ask-Camper trick */}
      <TryAILanguageRow />

      {/* WILD & HIDDEN RAIL — community-mapped, off-Pitchup picks */}
      <WildHiddenRail />

      <FeaturedCampersRail />

      <footer>
        <Footer />
      </footer>
    </div>
  );
}

const Stat = ({ k, v, light }) => (
  <div className={`${light ? "border-l-4 border-[#ff6a2c] pl-2.5 sm:pl-4" : ""} min-w-0`}>
    <div className={`font-display font-semibold text-xl sm:text-3xl lg:text-5xl leading-[1.05] whitespace-nowrap ${light ? "text-white" : ""}`}>{k}</div>
    <div className={`mt-1.5 sm:mt-2 text-[9px] sm:text-xs font-bold uppercase tracking-[0.06em] sm:tracking-[0.08em] leading-snug ${light ? "text-white" : "text-[#555]"}`}>{v}</div>
  </div>
);

const Feat = ({ icon: Icon, title, body, border }) => (
  <div className={`p-8 bg-white ${border ? "md:border-l hairline" : ""}`}>
    <Icon className="w-6 h-6 text-[#ff4500] mb-6" strokeWidth={1.5} />
    <h3 className="font-display text-xl mb-3">{title}</h3>
    <p className="text-sm text-[#555] leading-relaxed">{body}</p>
  </div>
);

/**
 * Multilingual chatbot teaser — three flag-chips that pre-fill Ask Camper
 * with a language-native sample question. Hooks straight into the same
 * `cc:chat:ask` window event the campsite detail chips already use, so
 * the chatbot widget opens and auto-sends without any extra wiring.
 */
function TryAILanguageRow() {
  const { t } = useTranslation();
  const ask = (questionKey) => {
    const text = t(questionKey);
    if (typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent("cc:chat:ask", { detail: { text } }));
    }
  };
  return (
    <section className="border-b hairline bg-white" data-testid="try-ai-row">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-14">
        <div className="flex items-start gap-4 max-w-3xl">
          <div className="hidden sm:flex items-center justify-center w-10 h-10 border hairline bg-[#fff4ee] text-[#ff4500] shrink-0">
            <Globe className="w-5 h-5" strokeWidth={1.75} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="eyebrow mb-2">{t("landing.tryAI_eyebrow")}</div>
            <p className="text-sm sm:text-base text-[#444] leading-snug mb-5 max-w-2xl">
              {t("landing.tryAI_body")}
            </p>
            <div className="flex flex-wrap gap-3">
              <button
                type="button"
                onClick={() => ask("landing.tryAI_sample_en")}
                className="inline-flex items-center gap-2 px-4 py-2.5 border hairline bg-white hover:bg-[#fff4ee] hover:border-[#ff4500] transition-colors text-sm"
                data-testid="try-ai-en"
              >
                <span className="text-base leading-none">🇬🇧</span> {t("landing.tryAI_en")}
              </button>
              <button
                type="button"
                onClick={() => ask("landing.tryAI_sample_fr")}
                className="inline-flex items-center gap-2 px-4 py-2.5 border hairline bg-white hover:bg-[#fff4ee] hover:border-[#ff4500] transition-colors text-sm"
                data-testid="try-ai-fr"
              >
                <span className="text-base leading-none">🇫🇷</span> {t("landing.tryAI_fr")}
              </button>
              <button
                type="button"
                onClick={() => ask("landing.tryAI_sample_de")}
                className="inline-flex items-center gap-2 px-4 py-2.5 border hairline bg-white hover:bg-[#fff4ee] hover:border-[#ff4500] transition-colors text-sm"
                data-testid="try-ai-de"
              >
                <span className="text-base leading-none">🇩🇪</span> {t("landing.tryAI_de")}
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
